/**
 * A tiny key/value store on the browser's IndexedDB, shaped like the storage the
 * React Query persister expects (getItem / setItem / removeItem, all async).
 *
 * Saved books used to live in localStorage, which browsers cap at roughly 5 MB per
 * site. Three or four books filled it, and every later save failed without a word,
 * so the device kept serving whatever copy last fit. IndexedDB allows hundreds of
 * MB. A failed write here is logged, never silent.
 */

const DB_NAME = 'shanenli-cache';
const STORE = 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    // A failed open (private mode in some browsers) must not stick forever
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

function run<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = action(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(request.result as T);
        tx.onerror = () => reject(tx.error ?? request.error);
        tx.onabort = () => reject(tx.error ?? request.error);
      })
  );
}

export const idbStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      const value = await run<string | undefined>('readonly', (s) => s.get(key));
      return value ?? null;
    } catch (error) {
      console.error('[idbStorage] read failed:', error);
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      await run('readwrite', (s) => s.put(value, key));
    } catch (error) {
      console.error('[idbStorage] save failed, books will download again next visit:', error);
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      await run('readwrite', (s) => s.delete(key));
    } catch (error) {
      console.error('[idbStorage] remove failed:', error);
    }
  },
};
